import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { Store } from "@ngrx/store";
import { Subscription, interval } from "rxjs";
import { filter } from "rxjs/operators";
import { Helper } from "src/app/core/helpers/helper";
import { RootReducerState } from "src/app/store";
import { changeMode } from "src/app/store/layouts/layout-action";
import { AuthService } from "src/app/shared/services/auth.service";
import { BookingService } from "src/app/shared/services/booking.service";
import { VehicleTrackingService } from "src/app/shared/services/vehicle-tracking.service";
import { SHOW_ADMIN_LOGS } from "src/app/layouts/sidebar/menu";
import { RheroShellBridge, ShellSearchHit } from "./rhero-shell.bridge";
import { RHERO_THEMES, applyRheroTheme, bootRheroTheme, rheroThemeById } from "./rhero-theme";
import { DispatcherScopeService } from "src/app/core/services/dispatcher-scope.service";

const PAGE_TITLES: { test: (url: string) => boolean; page: string }[] = [
  { test: (url) => url.startsWith("/users/dispatcher-panel") || url.startsWith("/users/quick-booking"), page: "Dispatch" },
  { test: (url) => url.startsWith("/users/history/running"), page: "Running" },
  { test: (url) => url.startsWith("/users/history/completed"), page: "Completed" },
  { test: (url) => url.startsWith("/users/history/scheduled"), page: "Scheduled" },
  { test: (url) => url.startsWith("/users/history/cancelled"), page: "Cancelled" },
  { test: (url) => url.startsWith("/users/recurring"), page: "Recurring" },
  { test: (url) => url.startsWith("/users/vehicle-tracking"), page: "V-Track" },
  { test: (url) => url.startsWith("/users/exception"), page: "Exception Logs" },
  { test: (url) => url.startsWith("/users/block-list-logs"), page: "Block List Logs" },
  { test: (url) => url.startsWith("/users/change-log"), page: "Change Log" },
  { test: (url) => url.startsWith("/users/api-log"), page: "API Log" },
  { test: (url) => url.startsWith("/users/airport-api-log"), page: "Airport API Log" },
  { test: (url) => url.startsWith("/users/booking-logs"), page: "Booking Logs" },
  { test: (url) => url.startsWith("/users/trip-details"), page: "Trip" },
  { test: (url) => url.startsWith("/users/profile"), page: "Profile" },
  { test: (url) => url.startsWith("/users/block-list"), page: "Block list" }
];

@Component({
  selector: "app-rhero-shell",
  templateUrl: "./rhero-shell.component.html",
  standalone: false
})
export class RheroShellComponent implements OnInit, OnDestroy {
  readonly showAdminLogs = SHOW_ADMIN_LOGS;
  bookingsOpen = false;
  logsOpen = false;
  userMenu = false;
  themeMenu = false;
  readonly themes = RHERO_THEMES;
  themeId = "rhero-light";
  shellAlertsOpen = false;
  shellPaletteOpen = false;
  paletteQuery = "";
  searchQuery = "";
  searchOpen = false;
  driversOpen = false;
  vehiclesOpen = false;
  driverQuery = "";
  vehicleQuery = "";
  weatherTemp = "";
  weatherIcon = "";
  /** sun | moon | partly | partly-night | cloud | fog | rain | snow | storm */
  weatherKind = "cloud";
  weatherText = "";
  clockNow = Date.now();
  shellAlerts: any[] = [];
  private url = "/users/dispatcher-panel";
  private subs: Subscription[] = [];

  constructor(
    public helper: Helper,
    public bridge: RheroShellBridge,
    private router: Router,
    private title: Title,
    private store: Store<RootReducerState>,
    private authService: AuthService,
    private bookingService: BookingService,
    private vehicleTrackingService: VehicleTrackingService,
    public scope: DispatcherScopeService
  ) {
    const theme = bootRheroTheme();
    this.themeId = theme.id;
    this.helper.isDarkModeOn = theme.mode === "dark";
  }

  ngOnInit(): void {
    this.watchPopovers();
    this.pinFavicon();
    this.url = this.router.url.split("?")[0];
    this.writeTitle();
    this.subs.push(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
        this.url = (event as NavigationEnd).urlAfterRedirects.split("?")[0];
        this.bookingsOpen = false;
        this.logsOpen = false;
        this.userMenu = false;
        this.themeMenu = false;
        this.shellAlertsOpen = false;
        this.shellPaletteOpen = false;
        this.searchOpen = false;
        this.driversOpen = false;
        this.vehiclesOpen = false;
        this.writeTitle();
      })
    );
    this.subs.push(interval(1000).subscribe(() => (this.clockNow = Date.now())));
    this.loadWeather();
    this.subs.push(interval(30 * 60 * 1000).subscribe(() => this.loadWeather()));
    this.refreshCounts();
    this.subs.push(this.scope.selectedCity$.subscribe((city) => {
      if (!city) return;
      this.closeMenus();
      this.bridge.publishDrivers([]);
      this.bridge.syncOnlineFromDrivers();
      this.bridge.driverParams = null;
      this.bridge.searchHits = [];
      this.bridge.finderType = "";
      this.shellAlerts = [];
      this.refreshCounts();
      this.loadWeather();
    }));
    this.subs.push(interval(30000).subscribe(() => this.refreshCounts()));
    document.addEventListener("keydown", this.onKey, true);
  }

  ngOnDestroy(): void {
    this.popObserver?.disconnect();
    window.removeEventListener("resize", this.clampSoon);
    this.subs.forEach((sub) => sub.unsubscribe());
    document.removeEventListener("keydown", this.onKey, true);
  }

  @HostListener("document:click")
  closeMenus(): void {
    this.bookingsOpen = false;
    this.logsOpen = false;
    this.userMenu = false;
    this.themeMenu = false;
    this.shellAlertsOpen = false;
    this.driversOpen = false;
    this.vehiclesOpen = false;
    this.searchOpen = false;
  }

  get onlineCount(): number {
    return this.bridge.online;
  }

  get alertCount(): number {
    return this.bridge.alertCount || this.shellAlerts.length;
  }

  clockLabel(): string {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: this.scope.currentCity()?.timezone || this.helper.displayTimezone || "America/Edmonton",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    }).format(this.clockNow);
  }

  dateLabel(): string {
    return new Intl.DateTimeFormat("en-US", { timeZone: this.scope.currentCity()?.timezone || this.helper.displayTimezone || "America/Edmonton", month: "short", day: "numeric" }).format(this.clockNow);
  }

  isActive(key: "dispatch" | "running" | "vtrack" | "reports" | "bookings" | "logs"): boolean {
    const url = this.url;
    if (key === "dispatch") return url.startsWith("/users/dispatcher-panel") || url.startsWith("/users/quick-booking");
    if (key === "running") return url.startsWith("/users/history/running");
    if (key === "vtrack") return url.startsWith("/users/vehicle-tracking");
    if (key === "reports") return url.startsWith("/users/history/completed");
    if (key === "bookings") {
      if (url.startsWith("/users/history/completed") || url.startsWith("/users/history/running")) return false;
      return (
        url.startsWith("/users/history/") ||
        url.startsWith("/users/recurring") ||
        url.startsWith("/users/trip-details")
      );
    }
    return ["/users/exception", "/users/block-list-logs", "/users/change-log", "/users/api-log", "/users/airport-api-log", "/users/booking-logs"].some((path) =>
      url.startsWith(path)
    );
  }

  toggleBookings(event: Event): void {
    event.stopPropagation();
    this.bookingsOpen = !this.bookingsOpen;
    this.logsOpen = false;
    this.userMenu = false;
    this.themeMenu = false;
    this.shellAlertsOpen = false;
    this.driversOpen = false;
    this.searchOpen = false;
  }

  toggleLogs(event: Event): void {
    event.stopPropagation();
    this.logsOpen = !this.logsOpen;
    this.bookingsOpen = false;
    this.userMenu = false;
    this.themeMenu = false;
    this.shellAlertsOpen = false;
    this.driversOpen = false;
    this.searchOpen = false;
  }

  toggleDrivers(event: Event): void {
    event.stopPropagation();
    this.driversOpen = !this.driversOpen;
    this.vehiclesOpen = false;
    this.bookingsOpen = false;
    this.logsOpen = false;
    this.userMenu = false;
    this.themeMenu = false;
    this.shellAlertsOpen = false;
    this.searchOpen = false;
    if (this.driversOpen) this.focusFinder("drivers-search");
  }

  toggleVehicles(event: Event): void {
    event.stopPropagation();
    this.vehiclesOpen = !this.vehiclesOpen;
    this.driversOpen = false;
    this.bookingsOpen = false;
    this.logsOpen = false;
    this.userMenu = false;
    this.themeMenu = false;
    this.shellAlertsOpen = false;
    this.searchOpen = false;
    if (this.vehiclesOpen) this.focusFinder("vehicles-search");
  }

  onFinderEsc(event: Event, which: "drivers" | "vehicles"): void {
    event.preventDefault();
    event.stopPropagation();
    if (which === "drivers") {
      if (this.driverQuery) this.driverQuery = "";
      else this.driversOpen = false;
      return;
    }
    if (this.vehicleQuery) this.vehicleQuery = "";
    else {
      this.vehiclesOpen = false;
      this.setFinderType("");
    }
  }

  setFinderType(typeName: string): void {
    this.bridge.finderType = typeName;
    this.bridge.onHighlightType?.(typeName);
  }

  vehicleTypes(): string[] {
    const names = new Set<string>();
    this.bridge.drivers.forEach((driver) => {
      const name = this.vehicleTypeName(driver);
      if (name) names.add(name);
    });
    return Array.from(names).sort();
  }

  filteredVehicles(): any[] {
    const q = this.vehicleQuery.trim().toLowerCase();
    const type = this.bridge.finderType;
    return this.bridge.drivers
      .map((driver) => this.vehicleRow(driver))
      .filter((row) => !type || row.typeName === type)
      .filter((row) => !q || row.blob.includes(q));
  }

  finderLegend(): string {
    const rows = this.filteredVehicles();
    const type = this.bridge.finderType || "Fleet";
    const online = rows.filter((row) => row.tone === "available").length;
    const onTrip = rows.filter((row) => row.tone === "ontrip" || row.tone === "busy").length;
    const offline = rows.filter((row) => row.tone === "offline").length;
    return `${type}: ${online} online, ${onTrip} on trip, ${offline} offline`;
  }

  pickVehicle(row: any): void {
    this.vehiclesOpen = false;
    this.bridge.onDriver?.(row.driver);
  }

  @HostListener("document:keydown", ["$event"])
  onShellEsc(event: KeyboardEvent): void {
    if (event.key !== "Escape") return;
    const target = event.target as HTMLElement | null;
    if (target?.id === "drivers-search" || target?.id === "vehicles-search") return;
    if (this.driverQuery && this.driversOpen) this.driverQuery = "";
    else if (this.driversOpen) this.driversOpen = false;
    if (this.vehicleQuery && this.vehiclesOpen) this.vehicleQuery = "";
    else if (this.vehiclesOpen) this.vehiclesOpen = false;
  }

  toggleUser(event: Event): void {
    event.stopPropagation();
    this.userMenu = !this.userMenu;
    this.themeMenu = false;
    this.bookingsOpen = false;
    this.logsOpen = false;
    this.shellAlertsOpen = false;
    this.driversOpen = false;
  }

  openSearch(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.searchOpen = true;
    this.driversOpen = false;
    this.bookingsOpen = false;
    this.logsOpen = false;
    this.userMenu = false;
    this.themeMenu = false;
    setTimeout(() => document.getElementById("rhero-header-search")?.focus(), 30);
  }

  onSearchInput(): void {
    this.searchOpen = true;
    const query = this.searchQuery.trim();
    if (this.bridge.onSearch) this.bridge.onSearch(query);
    else this.paletteQuery = query;
  }

  hits(): ShellSearchHit[] {
    if (this.bridge.onSearch) return this.bridge.searchHits;
    return this.paletteItems().map((item) => ({ id: item.link, label: item.label, hint: item.hint, kind: "page" as const, link: item.link }));
  }

  pickHit(hit: ShellSearchHit): void {
    this.searchOpen = false;
    this.searchQuery = "";
    this.bridge.searchHits = [];
    if (this.bridge.onPick && hit.kind !== "page") {
      this.bridge.onPick(hit);
      return;
    }
    if (hit.link) this.go(hit.link);
  }

  pickFirst(): void {
    const first = this.hits()[0];
    if (first) this.pickHit(first);
  }

  toneCount(tone: string): number {
    return this.bridge.drivers.filter((driver) => driver.tone === tone).length;
  }

  headerDrivers(): any[] {
    const filter = this.bridge.driverFilter;
    const q = this.driverQuery.trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    return this.bridge.drivers.filter((driver) => {
      if (filter === "active" && driver.tone !== "available") return false;
      if (filter === "busy" && driver.tone !== "ontrip" && driver.tone !== "busy") return false;
      if (!q) return true;
      const blob = this.driverBlob(driver);
      return blob.includes(q) || (digits.length >= 3 && this.driverDigits(driver).includes(digits));
    });
  }

  private focusFinder(id: string): void {
    setTimeout(() => document.getElementById(id)?.focus(), 30);
  }

  private vehicleTypeName(driver: any): string {
    return String(driver?.typeName || driver?.vehicleType?.name || driver?.vehicleTypeDetail?.name || driver?.vehicleDetail?.vehicleType?.name || driver?.vehicleDetail?.name || "").trim();
  }

  private vehicleRow(driver: any): any {
    const plate = String(driver?.plate || driver?.vehicleDetail?.plateNo || driver?.car || "").trim();
    const vehicleNo = String(driver?.vehicleDetail?.uniqueId || driver?.vehicleDetail?.vehicleNo || driver?.vehicleNo || "").trim();
    const make = String(driver?.vehicleDetail?.brandName || driver?.vehicleDetail?.brand || driver?.vehicleDetail?.make || "").trim();
    const model = String(driver?.vehicleDetail?.modelName || driver?.vehicleDetail?.model || "").trim();
    const typeName = this.vehicleTypeName(driver);
    const driverName = String(driver?.label || driver?.name || driver?.fullName || "Driver");
    const loc = driver?.location;
    const noLocation = loc?.latitude == null || loc?.longitude == null;
    const blob = [plate, vehicleNo, make, model, typeName, driverName].join(" ").toLowerCase();
    return { id: String(driver?._id || plate || driverName), plate, vehicleNo, typeName, driverName, tone: driver?.tone || "offline", noLocation, blob, driver };
  }

  private driverBlob(driver: any): string {
    return [
      driver?.label, driver?.name, driver?.fullName, driver?.firstName, driver?.lastName,
      driver?.phone, driver?.uniqueId, driver?._id, driver?.car,
      driver?.vehicleDetail?.plateNo, driver?.vehicleType?.name, driver?.typeName, this.vehicleTypeName(driver)
    ].map((part) => String(part || "").toLowerCase()).join(" ");
  }

  private driverDigits(driver: any): string {
    return `${driver?.phone || ""}${driver?.uniqueId || ""}`.replace(/\D/g, "");
  }

  toneLabel(tone: string): string {
    if (tone === "available") return "Available";
    if (tone === "ontrip") return "On trip";
    if (tone === "busy") return "Busy";
    return "Offline";
  }

  pickHeaderDriver(driver: any): void {
    this.driversOpen = false;
    this.bridge.onDriver?.(driver);
  }

  messageHeaderDriver(driver: any, event: Event): void {
    event.stopPropagation();
    this.driversOpen = false;
    this.bridge.onMessageDriver?.(driver);
  }

  toggleAlerts(event: Event): void {
    event.stopPropagation();
    if (this.bridge.alerts) {
      this.bridge.alerts();
      return;
    }
    this.shellAlertsOpen = !this.shellAlertsOpen;
    this.userMenu = false;
    this.themeMenu = false;
    this.bookingsOpen = false;
    this.logsOpen = false;
  }

  toggleTheme(event: Event): void {
    event.stopPropagation();
    const current = rheroThemeById(this.themeId);
    this.chooseTheme(current.mode === "dark" ? "rhero-light" : "rhero-dark");
  }

  toggleThemes(event: Event): void {
    event.stopPropagation();
    this.themeMenu = !this.themeMenu;
    this.userMenu = false;
    this.bookingsOpen = false;
    this.logsOpen = false;
    this.shellAlertsOpen = false;
    this.driversOpen = false;
    this.searchOpen = false;
  }

  currentSwatch(): string {
    return rheroThemeById(this.themeId).swatch;
  }

  chooseTheme(id: string): void {
    const theme = applyRheroTheme(id);
    this.themeId = theme.id;
    const mode = theme.mode === "dark" ? "dark" : "light";
    this.store.dispatch(changeMode({ mode }));
    this.helper.isDarkModeOn = theme.mode === "dark";
    this.helper.themeMode.next({});
    this.themeMenu = false;
    this.userMenu = false;
  }

  fullscreen(): void {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => undefined);
    else document.exitFullscreen().catch(() => undefined);
  }

  logout(): void {
    const entity = this.helper.dispatcherDetails?.entity || {};
    this.authService
      .logOut({
        loginBy: this.helper.LoginBy.PHONE,
        countryPhoneCode: entity.countryPhoneCode,
        phone: entity.phone
      })
      .subscribe();
  }

  initials(): string {
    try {
      const raw = localStorage.getItem("userData") || localStorage.getItem("entity") || "";
      const data = raw ? JSON.parse(raw) : {};
      const name = data?.firstName || data?.name || data?.entity?.firstName || "Dispatcher";
      return String(name).slice(0, 2).toUpperCase();
    } catch {
      return "DK";
    }
  }

  paletteItems(): { label: string; hint: string; link: string }[] {
    const items = [
      { label: "Dispatch", hint: "Desk", link: "/users/dispatcher-panel" },
      { label: "Running", hint: "Bookings", link: "/users/history/running" },
      { label: "Completed", hint: "Bookings", link: "/users/history/completed" },
      { label: "Scheduled", hint: "Bookings", link: "/users/history/scheduled" },
      { label: "Cancelled", hint: "Bookings", link: "/users/history/cancelled" },
      { label: "Recurring", hint: "Bookings", link: "/users/recurring" },
      { label: "Exception Logs", hint: "Logs", link: "/users/exception" },
      { label: "Block List Logs", hint: "Logs", link: "/users/block-list-logs" },
      { label: "Reports", hint: "Completed", link: "/users/history/completed" },
      { label: "Profile", hint: "Account", link: "/users/profile" }
    ];
    const q = this.paletteQuery.trim().toLowerCase();
    return q ? items.filter((item) => `${item.label} ${item.hint}`.toLowerCase().includes(q)) : items;
  }

  openMessages(event: Event): void {
    event.stopPropagation();
    this.vehiclesOpen = false;
    this.driversOpen = false;
    this.bookingsOpen = false;
    this.logsOpen = false;
    this.userMenu = false;
    if (this.bridge.onMessages) {
      this.bridge.onMessages();
      return;
    }
    this.router.navigate(["/users/dispatcher-panel"], { queryParams: { messages: 1 } });
  }

  go(link: string): void {
    this.shellPaletteOpen = false;
    this.router.navigateByUrl(link);
  }

  alertLabel(alert: any): string {
    const id = alert?.bookingUniqueId ? `#${alert.bookingUniqueId}` : "";
    if (alert?.type === 1) return `${id || alert.driverName || "Driver"} SOS`;
    if (alert?.type === 3) return `${id || "Driver"} no response`;
    return `${id || "Trip"} late`;
  }

  private onKey = (event: KeyboardEvent): void => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      event.stopPropagation();
      this.openSearch();
    }
    if (event.key === "Escape") {
      this.shellPaletteOpen = false;
      this.searchOpen = false;
      this.closeMenus();
    }
  };

  /** Keep every header popover inside the viewport (8px margin); the theme menu is right-aligned to its trigger. */
  private popObserver: MutationObserver | null = null;
  private clampQueued = false;
  private clampSoon = (): void => {
    if (this.clampQueued) return;
    this.clampQueued = true;
    requestAnimationFrame(() => {
      this.clampQueued = false;
      this.clampPopovers();
    });
  };

  private watchPopovers(): void {
    setTimeout(() => {
      const host = document.getElementById("rhero-shell");
      if (!host || typeof MutationObserver === "undefined") return;
      this.popObserver = new MutationObserver(this.clampSoon);
      this.popObserver.observe(host, { childList: true, subtree: true });
      window.addEventListener("resize", this.clampSoon);
    }, 0);
  }

  private clampPopovers(): void {
    const margin = 8;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const theme = document.querySelector("#rhero-shell .theme-menu") as HTMLElement | null;
    const trigger = document.querySelector("#rhero-shell .theme-picker-button") as HTMLElement | null;
    if (theme && trigger) {
      const t = trigger.getBoundingClientRect();
      const w = theme.offsetWidth;
      const left = Math.max(margin, Math.min(t.right - w, vw - margin - w));
      theme.style.position = "fixed";
      theme.style.top = `${Math.round(t.bottom + 6)}px`;
      theme.style.left = `${Math.round(left)}px`;
      theme.style.right = "auto";
    }
    document.querySelectorAll<HTMLElement>("#rhero-shell .header-pop, #rhero-shell .nav-drop-menu, #rhero-shell .header-search-menu").forEach((pop) => {
      pop.style.translate = "";
      const r = pop.getBoundingClientRect();
      if (!r.width) return;
      let dx = 0;
      if (r.right > vw - margin) dx = vw - margin - r.right;
      if (r.left + dx < margin) dx = margin - r.left;
      if (dx) pop.style.translate = `${Math.round(dx)}px 0`;
    });
  }

  private loadWeather(): void {
    const cityId = this.scope.currentCityId();
    const coords = this.scope.currentCity()?.location?.coordinates;
    this.weatherTemp = "";
    if (!coords) return;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords[1]}&longitude=${coords[0]}&current=temperature_2m,weather_code,is_day`;
    fetch(url)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cityId !== this.scope.currentCityId()) return;
        const temp = data?.current?.temperature_2m;
        if (temp == null) return;
        this.weatherTemp = `${Math.round(Number(temp))}°`;
        const code = Number(data?.current?.weather_code);
        const day = Number(data?.current?.is_day ?? 1) === 1;
        this.weatherTemp = `${Math.round(Number(temp))}°C`;
        this.weatherIcon = this.weatherGlyph(code);
        this.weatherKind = this.weatherKindOf(code, day);
        this.weatherText = this.weatherLabel(code, day);
      })
      .catch(() => undefined);
  }

  private weatherKindOf(code: number, day: boolean): string {
    if (code === 0) return day ? "sun" : "moon";
    if (code <= 2) return day ? "partly" : "partly-night";
    if (code === 3) return "cloud";
    if (code === 45 || code === 48) return "fog";
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return "rain";
    if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "snow";
    if (code >= 95) return "storm";
    return "cloud";
  }

  private weatherLabel(code: number, day: boolean): string {
    if (code === 0) return day ? "Sunny" : "Clear";
    if (code === 1) return day ? "Mostly sunny" : "Mostly clear";
    if (code === 2) return "Partly cloudy";
    if (code === 3) return "Overcast";
    if (code === 45 || code === 48) return "Fog";
    if (code >= 51 && code <= 57) return "Drizzle";
    if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return "Rain";
    if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "Snow";
    if (code >= 95) return "Thunderstorm";
    return "Cloudy";
  }

  private weatherGlyph(code: number): string {
    if (code === 0) return "☀";
    if (code <= 3) return "⛅";
    if (code === 45 || code === 48) return "🌫";
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return "🌧";
    if (code >= 71 && code <= 77) return "❄";
    if (code >= 95) return "⛈";
    return "☁";
  }

  private writeTitle(): void {
    const match = PAGE_TITLES.find((row) => row.test(this.url));
    this.title.setTitle(`R-Hero Dispatch – ${match?.page || "Dispatch"}`);
  }

  private pinFavicon(): void {
    document.querySelectorAll("link[rel='icon']").forEach((node) => {
      const link = node as HTMLLinkElement;
      const href = link.getAttribute("href") || "";
      if (/r-hero-logo-full|titleLogo|\.jpe?g/i.test(href)) link.remove();
    });
  }

  private refreshCounts(): void {
    const cityId = this.scope.currentCityId();
    // Off the desk the header loads the same driver list the desk uses (same endpoint and scope),
    // so the online pill, Drivers count, Drivers panel and Vehicles panel always agree.
    if (this.bridge.palette) return this.refreshAlerts();
    const params = { ...(this.bridge.driverParams || { businessType: this.helper.BusinessType?.TAXI ?? 1, includeOffline: "true" }), isShowLoader: false, isShowErrorToast: false };
    this.vehicleTrackingService.listActiveDrivers(params).subscribe({
      next: (res: any) => {
        if (cityId !== this.scope.currentCityId()) return;
        if (this.bridge.palette) return;
        const drivers = res?.data?.data?.drivers || res?.data?.drivers || [];
        this.bridge.publishDrivers(drivers.map((driver: any) => RheroShellBridge.decorate(driver)));
        this.bridge.syncOnlineFromDrivers();
      },
      error: () => undefined
    });
    this.refreshAlerts();
  }

  private refreshAlerts(): void {
    const cityId = this.scope.currentCityId();
    this.bookingService.listDispatcherAlerts().subscribe({
      next: (rows: any[]) => {
        if (cityId !== this.scope.currentCityId()) return;
        this.shellAlerts = rows || [];
        if (!this.bridge.alerts) this.bridge.setAlertCount(this.shellAlerts.length);
      },
      error: () => undefined
    });
  }
}
